import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PAYMENT_METHODS, formatPaise, toPaise } from '@pharmacia/core'
import { ApiError, del, get, post } from '../lib/api.ts'
import { formatDateTime } from '../lib/format.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { Modal } from '../components/Modal.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { CancelBillDialog } from '../components/CancelBillDialog.tsx'
import { BillStatus } from '../components/BillStatus.tsx'
import { useToast } from '../components/Toast.tsx'
import { useAuth } from '../lib/auth.tsx'
import { Button, Field, Input, Select, Skeleton } from '../components/ui.tsx'
import { IconAlert, IconCheck } from '../components/icons.tsx'

type Line = {
  lineNo: number; productName: string; quantity: string; unitPrice: string
  cgstRate: string; sgstRate: string; taxable: string
  cgstAmount: string; sgstAmount: string; lineTotal: string
}
type Data = {
  bill: Record<string, unknown>
  lines: Line[]
  subtotal: string; cgst: string; sgst: string; total: string
  live: boolean; diverged: boolean
  fingerprint?: string
}

/** How often an open draft asks whether the prescription has moved. */
const POLL_MS = 8000

const money = (v: string) => formatPaise(toPaise(v))

export function BillDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const toast = useToast()
  const { can } = useAuth()
  // Discarding a draft and cancelling an issue are the proprietor's calls.
  const mayVoid = can('billing:void')
  const [data, setData] = useState<Data | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [paying, setPaying] = useState(false)

  // Arriving from the list's "Mark paid" button opens the dialog straight
  // away, so taking payment is one click rather than three.
  useEffect(() => {
    if (params.get('pay') !== '1' || !data) return
    if (data.bill['status'] === 'final' && !data.bill['paid_at']) setPaying(true)
    setParams({}, { replace: true })
  }, [params, data, setParams])
  const [changed, setChanged] = useState(false)
  const seen = useRef<string | null>(null)

  const load = useCallback((quiet = false) => {
    let cancelled = false
    get<Data>(`/api/bills/${id}`)
      .then(d => {
        if (cancelled) return
        setData(d)
        seen.current = d.fingerprint ?? null
        if (quiet) setChanged(false)
      })
      .catch((e: ApiError) => { if (!cancelled && !quiet) toast.fail('Could not load bill', e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, toast])

  useEffect(() => load(), [load])

  /**
   * A draft follows the prescription, but only if the screen looking at
   * it stays current. Recomputing on the server is not enough on its own:
   * reception could have the bill open, the doctor could change it, and
   * the figures on screen would be stale until somebody happened to
   * reload -- which is exactly the moment money changes hands.
   *
   * So an open draft asks every few seconds whether anything moved, and
   * again whenever the window is brought back to the front. Polling stops
   * while the tab is hidden, because a screen nobody is looking at cannot
   * mislead anyone.
   */
  const isDraft = data?.bill['status'] === 'draft'
  useEffect(() => {
    if (!isDraft) return
    let stopped = false

    async function check() {
      if (stopped || document.hidden) return
      try {
        const s = await get<{ status: string; fingerprint: string | null }>(`/api/bills/${id}/state`)
        if (stopped) return
        // The bill itself may also have been issued or cancelled in
        // another window.
        if (s.status !== 'draft') { load(true); return }
        if (s.fingerprint && seen.current && s.fingerprint !== seen.current) {
          setChanged(true)
          load()
        }
      } catch { /* a failed poll is not worth interrupting anyone over */ }
    }

    const timer = window.setInterval(check, POLL_MS)
    const onFocus = () => void check()
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      stopped = true
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [isDraft, id, load])

  if (loading) {
    return (
      <>
        <PageHeader title="Bill" />
        <div className="content"><div className="page-max card"><div className="card-body stack" style={{ gap: 'var(--s-4)' }}>
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} h={22} />)}
        </div></div></div>
      </>
    )
  }
  if (!data) return null

  const b = data.bill
  const status = String(b['status']) as 'draft' | 'final' | 'cancelled'

  async function finalise() {
    setBusy(true)
    try {
      const res = await post<{ number: string; total: string }>(`/api/bills/${id}/finalise`,
        {
          version: Number(b['version']),
          // What the operator was actually looking at. The server refuses
          // if the prescription moved since this was rendered.
          fingerprint: data?.fingerprint,
        })
      toast.ok('Bill issued', `${res.number} · ${money(res.total)}`)
      setChanged(false)
      load(true)
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'BILL_CHANGED') {
        setChanged(true)
        load()
        toast.fail('The prescription changed', 'The figures have been refreshed. Check them and issue again.')
      } else {
        toast.fail('Could not issue the bill', err.message)
      }
    } finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader title={String(b['number'] ?? 'Draft bill')}>
        <BillStatus status={status} paidAt={b['paid_at'] as string | null} />
        {status === 'draft' ? (
          <>
            <Button variant="primary" onClick={finalise} loading={busy}>Issue bill</Button>
            {mayVoid ? (
              <Button variant="danger" onClick={() => setDiscarding(true)}>Discard</Button>
            ) : null}
          </>
        ) : null}
        {status === 'final' && !b['paid_at'] ? (
          <>
            <Button variant="primary" onClick={() => setPaying(true)}>Record payment</Button>
            {mayVoid ? (
              <Button variant="danger" onClick={() => setCancelling(true)}>Cancel bill</Button>
            ) : null}
          </>
        ) : null}
        {status === 'final' && b['paid_at'] && mayVoid ? (
          <Button variant="danger" onClick={() => setCancelling(true)}>Cancel bill</Button>
        ) : null}
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)', maxWidth: 880 }}>

          {changed ? (
            <div className="notice" role="alert">
              <span style={{ color: 'var(--danger)', marginTop: 1 }}><IconAlert size={15} /></span>
              <span>
                <strong>The prescription just changed.</strong> The figures below have
                been refreshed — check the total before taking payment.
              </span>
            </div>
          ) : null}

          {status === 'draft' && !changed ? (
            <div className="notice info">
              <span style={{ marginTop: 1 }}><IconCheck size={15} /></span>
              <span>
                This is a draft. It follows the prescription — if the doctor changes
                it, the figures here update within a few seconds. Issuing the bill
                fixes them.
              </span>
            </div>
          ) : null}

          {data.diverged ? (
            <div className="notice" role="alert">
              <span style={{ color: 'var(--danger)', marginTop: 1 }}><IconAlert size={15} /></span>
              <span>
                The prescription has changed since this bill was issued. The bill is
                <strong> deliberately unchanged</strong> — it was handed to the patient and
                reported as it stands. To correct it, cancel this bill and issue a new one.
              </span>
            </div>
          ) : null}

          <div className="card">
            <div className="card-body detail-grid">
              <div><span className="k">Patient</span><span className="v">{String(b['patient_name'])}</span></div>
              <div><span className="k">MRN</span><span className="v mono">{String(b['mrn'])}</span></div>
              <div><span className="k">Prescription</span><span className="v mono">{String(b['prescription_number'])}</span></div>
              <div><span className="k">Doctor</span><span className="v">{String(b['doctor_name'])}</span></div>
              <div><span className="k">{status === 'final' ? 'Issued' : 'Opened'}</span>
                <span className="v">{formatDateTime(b['issued_at'] ?? b['created_at'])}</span></div>
              {b['paid_at'] ? (
                <div><span className="k">Paid</span>
                  <span className="v">
                    {money(String(b['paid_amount']))} · {String(b['payment_method'])}
                  </span></div>
              ) : null}
              {b['cancel_reason'] ? (
                <div><span className="k">Cancelled because</span><span className="v">{String(b['cancel_reason'])}</span></div>
              ) : null}
              {status === 'cancelled' && b['paid_at'] ? (
                <div className="span-2">
                  <span className="k">Refund</span>
                  <span className="v" style={{ color: 'var(--danger)' }}>
                    {formatPaise(toPaise(String(b['paid_amount'] ?? b['total'] ?? '0')))} was taken
                    before this bill was cancelled. Cancelling does not return it — somebody
                    has to hand it back.
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead><tr>
                  <th style={{ width: 40 }}>#</th><th>Product</th>
                  <th className="money">Qty</th><th className="money">Rate</th>
                  <th className="money">Taxable</th>
                  <th className="money">CGST</th><th className="money">SGST</th>
                  <th className="money">Amount</th>
                </tr></thead>
                <tbody>
                  {data.lines.map(l => (
                    <tr key={l.lineNo}>
                      <td className="dim">{l.lineNo}</td>
                      <td style={{ fontWeight: 550 }}>{l.productName}</td>
                      <td className="money">{Number(l.quantity)}</td>
                      <td className="money">{money(l.unitPrice)}</td>
                      <td className="money">{money(l.taxable)}</td>
                      <td className="money dim">{money(l.cgstAmount)}<span style={{ fontSize: 'var(--text-xs)' }}> · {Number(l.cgstRate)}%</span></td>
                      <td className="money dim">{money(l.sgstAmount)}<span style={{ fontSize: 'var(--text-xs)' }}> · {Number(l.sgstRate)}%</span></td>
                      <td className="money" style={{ fontWeight: 650 }}>{money(l.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="totals">
              <span className="lbl">Taxable value</span><span className="amt">{money(data.subtotal)}</span>
              <span className="lbl">CGST</span><span className="amt">{money(data.cgst)}</span>
              <span className="lbl">SGST</span><span className="amt">{money(data.sgst)}</span>
              <span className="rule" />
              <span className="lbl grand">Total</span><span className="amt grand">{money(data.total)}</span>
            </div>
          </div>

          <p className="dim" style={{ fontSize: 'var(--text-xs)', maxWidth: '68ch', lineHeight: 1.7 }}>
            Intra-state supply, so tax splits into CGST and SGST. Each is rounded per
            line, so the total always equals the sum of the amounts printed above it.
          </p>
        </div>
      </div>

      {paying ? (
        <PayDialog billId={String(id)} version={Number(b['version'])} total={data.total}
          onClose={() => setPaying(false)}
          onDone={() => { setPaying(false); load(true); toast.ok('Payment recorded') }} />
      ) : null}

      {cancelling ? (
        <CancelBillDialog billId={String(id)} version={Number(b['version'])}
          paid={Boolean(b['paid_at'])}
          onClose={() => setCancelling(false)}
          onDone={() => { setCancelling(false); load(); toast.ok('Bill cancelled') }} />
      ) : null}

      {discarding ? (
        <ConfirmDialog
          title="Discard this draft?"
          body="Nothing has been issued, so nothing is lost. The prescription returns to the billing queue."
          confirmLabel="Discard" danger
          onClose={() => setDiscarding(false)}
          onConfirm={async () => {
            try {
              await del(`/api/bills/${id}`)
              toast.ok('Draft discarded')
              navigate('/billing')
            } catch (e) { toast.fail('Could not discard', (e as ApiError).message) }
          }}
        />
      ) : null}
    </>
  )
}

function PayDialog({ billId, version, total, onClose, onDone }: {
  billId: string; version: number; total: string; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [method, setMethod] = useState<string>('cash')
  // Defaults to the full amount, which is the usual case, but stays
  // editable so a short payment is recorded as what it was rather than
  // rounded up out of sight.
  const [amount, setAmount] = useState(total)
  const [busy, setBusy] = useState(false)

  return (
    <Modal
      title="Record payment"
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" loading={busy}
            onClick={async () => {
              setBusy(true)
              try {
                await post(`/api/bills/${billId}/pay`, { method, amount: amount.trim(), version })
                onDone()
              } catch (e) { toast.fail('Could not record the payment', (e as ApiError).message) }
              finally { setBusy(false) }
            }}>
            Mark paid
          </Button>
        </>
      }
    >
      <p className="muted" style={{ lineHeight: 1.7, marginBottom: 'var(--s-4)' }}>
        Once this is recorded the bill is settled and the prescription behind it
        closes to further changes. A correction after this means a new prescription.
      </p>
      <div className="form-grid">
        <Field label="Amount taken" htmlFor="pay-amount" hint={`Bill total ${money(total)}`}>
          <Input id="pay-amount" value={amount} inputMode="decimal" autoFocus
            onChange={e => setAmount(e.target.value)} />
        </Field>
        <Field label="Method" htmlFor="pay-method">
          <Select id="pay-method" value={method} onChange={e => setMethod(e.target.value)}>
            {PAYMENT_METHODS.map(m => (
              <option key={m} value={m}>{m === 'upi' ? 'UPI' : m[0]!.toUpperCase() + m.slice(1)}</option>
            ))}
          </Select>
        </Field>
      </div>
    </Modal>
  )
}
